import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Paper,
  TextField,
  Button,
  Typography,
  List,
  ListItem,
  Avatar,
  IconButton,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Divider,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  Send as SendIcon,
  SmartToy as BotIcon,
  Person as PersonIcon,
  Clear as ClearIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';

interface ChatMessage {
  id: string;
  text: string;
  isBot: boolean;
  timestamp: Date;
  promptUsed?: string;
}

interface AgentPrompt {
  _id: string;
  name: string;
  prompt: string;
  status: string;
}

const PromptTester: React.FC = () => {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [inputMessage, setInputMessage] = useState('');
  const [prompts, setPrompts] = useState<AgentPrompt[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const { user } = useAuth();

  // Connected account check
  const noConnectedAccounts = !user?.facebookPageId && !user?.instagramBusinessId && !user?.whatsappBusinessAccountId && !user?.tiktokId;

  // Scroll to bottom when new messages arrive
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Fetch agent prompts to show active count
  useEffect(() => {
    const fetchPrompts = async () => {
      try {
        const response = await fetch('/api/chatgpt-prompts');
        if (response.ok) {
          const data = await response.json();
          const activePrompts = data.filter((prompt: AgentPrompt) => prompt.status === 'active');
          setPrompts(activePrompts);
        }
      } catch (error) {
        console.error('Error fetching prompts:', error);
        setError('Failed to load prompts');
      }
    };

    if (!noConnectedAccounts) {
      fetchPrompts();
    }
  }, [noConnectedAccounts]);

  const sendMessage = async () => {
    if (!inputMessage.trim() || loading || prompts.length === 0) return;

    const userMessage: ChatMessage = {
      id: Date.now().toString(),
      text: inputMessage.trim(),
      isBot: false,
      timestamp: new Date(),
    };

    setMessages(prev => [...prev, userMessage]);
    setInputMessage('');
    setLoading(true);
    setError('');

    try {
      // Send message to test agent response (uses real auto-reply system)
      const response = await fetch('/api/test-agent-response', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          message: userMessage.text,
        }),
      });

      if (response.ok) {
        const data = await response.json();
        const botMessage: ChatMessage = {
          id: (Date.now() + 1).toString(),
          text: data.response,
          isBot: true,
          timestamp: new Date(),
          promptUsed: data.promptName,
        };

        setMessages(prev => [...prev, botMessage]);
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to get agent response');
      }
    } catch (error) {
      console.error('Error sending message:', error);
      setError('Failed to send message');
    } finally {
      setLoading(false);
    }
  };

  const clearChat = () => {
    setMessages([]);
    setError('');
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  if (!user) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning">Please log in to use the Agent Prompt Tester.</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column', position: 'relative' }}>

      {/* Active Prompts Info */}
      <Paper sx={{ p: 2, mb: 2, backgroundColor: '#f8f9fa', border: '1px solid #e9ecef' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box sx={{ 
            width: 12, 
            height: 12, 
            borderRadius: '50%', 
            backgroundColor: prompts.length > 0 ? '#10B981' : '#ef4444',
            animation: prompts.length > 0 ? 'pulse 2s infinite' : 'none',
            '@keyframes pulse': {
              '0%': { opacity: 1 },
              '50%': { opacity: 0.5 },
              '100%': { opacity: 1 },
            },
          }} />
          <Typography variant="body1" sx={{ fontWeight: 600 }}>
            {prompts.length > 0 
              ? `${prompts.length} Active Agent Prompt${prompts.length > 1 ? 's' : ''} Ready`
              : 'No Active Agent Prompts Found'
            }
          </Typography>
        </Box>
        <Typography variant="body2" sx={{ mt: 1, color: '#666', ml: 3.5 }}>
          {prompts.length > 0 
            ? 'The system will automatically use your active prompts to generate responses, just like with real customers.'
            : 'Please create and activate at least one prompt in Agent Prompts section to test responses.'
          }
        </Typography>
      </Paper>

      {/* Chat Area */}
      <Paper 
        sx={{ 
          flex: 1, 
          display: 'flex', 
          flexDirection: 'column',
          overflow: 'hidden',
          ...(noConnectedAccounts ? { filter: 'blur(1.2px)', pointerEvents: 'none', userSelect: 'none' } : {})
        }}
      >
        {/* Chat Header */}
        <Box sx={{ 
          p: 2, 
          borderBottom: '1px solid #e0e0e0',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <Typography variant="h6">Chat Simulation</Typography>
          <IconButton onClick={clearChat} size="small">
            <ClearIcon />
          </IconButton>
        </Box>

        {/* Messages */}
        <Box sx={{ 
          flex: 1, 
          overflowY: 'auto', 
          p: 1,
          backgroundColor: '#fafafa'
        }}>
          {messages.length === 0 ? (
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'center', 
              alignItems: 'center', 
              height: '100%',
              flexDirection: 'column'
            }}>
              <BotIcon sx={{ fontSize: 48, color: '#bdbdbd', mb: 2 }} />
              <Typography variant="body1" color="textSecondary">
                Start a conversation to test your agent prompts
              </Typography>
            </Box>
          ) : (
            <List sx={{ p: 0 }}>
              {messages.map((message) => (
                <ListItem
                  key={message.id}
                  sx={{
                    display: 'flex',
                    justifyContent: message.isBot ? 'flex-start' : 'flex-end',
                    alignItems: 'flex-start',
                    mb: 2,
                  }}
                >
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'flex-start',
                    flexDirection: message.isBot ? 'row' : 'row-reverse',
                    maxWidth: '70%'
                  }}>
                    <Avatar
                      sx={{
                        bgcolor: message.isBot ? '#5fd5b6' : '#2196f3',
                        width: 32,
                        height: 32,
                        mx: 1
                      }}
                    >
                      {message.isBot ? <BotIcon /> : <PersonIcon />}
                    </Avatar>
                    
                    <Box>
                      <Paper
                        sx={{
                          p: 2,
                          backgroundColor: message.isBot ? '#e8f5e8' : '#e3f2fd',
                          borderRadius: 2,
                        }}
                      >
                        <Typography variant="body1">
                          {message.text}
                        </Typography>
                        
                        {message.isBot && message.promptUsed && (
                          <Typography 
                            variant="caption" 
                            sx={{ 
                              display: 'block', 
                              mt: 1, 
                              color: '#666',
                              fontStyle: 'italic'
                            }}
                          >
                            Used prompt: {message.promptUsed}
                          </Typography>
                        )}
                      </Paper>
                      
                      <Typography 
                        variant="caption" 
                        sx={{ 
                          color: '#999', 
                          ml: message.isBot ? 0 : 'auto',
                          display: 'block',
                          textAlign: message.isBot ? 'left' : 'right',
                          mt: 0.5
                        }}
                      >
                        {message.timestamp.toLocaleTimeString()}
                      </Typography>
                    </Box>
                  </Box>
                </ListItem>
              ))}
              
              {loading && (
                <ListItem sx={{ display: 'flex', justifyContent: 'flex-start' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Avatar sx={{ bgcolor: '#5fd5b6', width: 32, height: 32, mr: 1 }}>
                      <BotIcon />
                    </Avatar>
                    <Paper sx={{ p: 2, backgroundColor: '#e8f5e8', borderRadius: 2 }}>
                      <CircularProgress size={16} sx={{ mr: 1 }} />
                      <Typography variant="body2" component="span">
                        Thinking...
                      </Typography>
                    </Paper>
                  </Box>
                </ListItem>
              )}
            </List>
          )}
          <div ref={messagesEndRef} />
        </Box>

        {/* Error Alert */}
        {error && (
          <Alert severity="error" sx={{ m: 1 }}>
            {error}
          </Alert>
        )}

        {/* Input Area */}
        <Divider />
        <Box sx={{ p: 2, display: 'flex', gap: 1 }}>
          <TextField
            fullWidth
            multiline
            maxRows={3}
            placeholder="Type a customer message to test agent response..."
            value={inputMessage}
            onChange={(e) => setInputMessage(e.target.value)}
            onKeyPress={handleKeyPress}
            disabled={loading || prompts.length === 0}
          />
          <Button
            variant="contained"
            onClick={sendMessage}
            disabled={loading || !inputMessage.trim() || prompts.length === 0}
            sx={{
              minWidth: 'auto',
              px: 2,
              backgroundColor: '#5fd5b6',
              '&:hover': {
                backgroundColor: '#4bc4a3',
              },
            }}
          >
            <SendIcon />
          </Button>
        </Box>
      </Paper>

      {noConnectedAccounts && (
        <Box sx={{
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          zIndex: 1300,
          pointerEvents: 'auto',
          userSelect: 'auto',
          width: '100%',
          maxWidth: 520
        }}>
          <Alert severity="warning" sx={{
            backgroundColor: '#FFF7E6',
            border: '1px solid #FFE1A3',
            color: '#8A6D3B',
            width: '100%',
            textAlign: 'center',
            p: 3,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, textAlign: 'center' }}>
              No Connected Accounts
            </Typography>
            <Typography sx={{ mb: 3 }}>
              Connect at least one social media account to test Agent responses.
            </Typography>
            <Button
              variant="contained"
              color="warning"
              onClick={() => window.dispatchEvent(new Event('open-connect-dropdown'))}
              sx={{ fontWeight: 600 }}
            >
              Connect Account
            </Button>
          </Alert>
        </Box>
      )}
    </Box>
  );
};

export default PromptTester;
